import {
  startAuthentication,
  type PublicKeyCredentialRequestOptionsJSON,
} from "@simplewebauthn/browser";
import { PasskeyAuthenticationVerifyRequestSchema } from "@oyako-flow/contracts";
import { useState } from "react";
import { createOyakoFlowClient } from "../../../clients/typescript/src/index";
import type { components } from "../../../clients/typescript/src/generated";
export function UnjoinedAccountDeletion() {
  const query = new URLSearchParams(location.search),
    requestId = query.get("account_cleanup") ?? "",
    hash = query.get("receipt_hash") ?? "",
    state = query.get("state") ?? "";
  const [confirmation, setConfirmation] = useState(""),
    [busy, setBusy] = useState(false),
    [error, setError] = useState<string | null>(null),
    [done, setDone] = useState(false);
  async function remove() {
    setBusy(true);
    setError(null);
    try {
      if (
        !/^[0-9a-f-]{36}$/iu.test(requestId) ||
        !/^\w{43,128}$/u.test(state.replaceAll("-", "_")) ||
        !/^([a-f0-9]{64})$/u.test(hash)
      )
        throw new Error("アプリの最初の画面から開き直してください。");
      const client = createOyakoFlowClient(""),
        headers = () => ({ "Idempotency-Key": crypto.randomUUID() });
      const options = await client.POST("/api/v1/account-lifecycle/passkey/options", {
        params: { header: headers() },
        body: {
          request_id: requestId,
          receipt_hash: hash,
          confirmation: "delete_unjoined_account",
        },
      });
      if (!options.data)
        throw new Error("本人確認を開始できませんでした。時間をおいてやり直してください。");
      const credential = await startAuthentication({
        optionsJSON: options.data.public_key as unknown as PublicKeyCredentialRequestOptionsJSON,
      });
      const body = PasskeyAuthenticationVerifyRequestSchema.parse({
        ceremony_id: options.data.ceremony_id,
        credential,
        device_label: "家族から退出したアカウントの削除",
      }) as components["schemas"]["PasskeyAuthenticationVerifyRequest"];
      const result = await client.POST("/api/v1/account-lifecycle/passkey/verify", {
        params: { header: headers() },
        body,
      });
      if (!result.data) throw new Error(result.error.error.message);
      setDone(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "本人確認できませんでした。");
    } finally {
      setBusy(false);
    }
  }
  return (
    <main className="auth-shell">
      <section className="auth-card">
        <p>Oyako Flow</p>
        <h1>家族から退出したアカウントの削除</h1>
        <p>
          参加している家族がなくなった保護者のアカウントを削除します。Passkey・回復コード・連絡先などを削除し、以前の家族に残る共有記録では匿名の保護者になります。元には戻せません。
        </p>
        <p>
          参加中の家族がある場合は、親アプリの「設定 →
          家族から抜ける・退会」で影響を確認してください。
        </p>
        {done ? (
          <>
            <p role="status">削除を受け付けました。処理状況はアプリで確認できます。</p>
            <a
              className="primary"
              href={`oyakoflow://account-deleted?request_id=${encodeURIComponent(requestId)}&state=${encodeURIComponent(state)}`}
            >
              アプリに戻る
            </a>
          </>
        ) : (
          <>
            <label>
              確認のため「削除する」と入力してください
              <input
                value={confirmation}
                onChange={(e) => setConfirmation(e.target.value)}
                autoComplete="off"
              />
            </label>
            <button disabled={busy || confirmation !== "削除する"} onClick={() => void remove()}>
              Passkeyで本人確認して削除する
            </button>
          </>
        )}
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
      </section>
    </main>
  );
}
